import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { filterTravelersByCondition, getPassengerLabel, isEmptyObject, notEqual } from '@common/helpers';
import {
  PassengerType,
  PREVIOUS_PAGE_IDENTIFIER,
  ServicingFunctionIdType,
  ServicingPageIdType,
} from '@common/interfaces';
import {
  ChangeBaggageData,
  PassengerApplicationStatusMap,
  SegmentMapData,
  UpdateServiceInformation,
} from '@common/interfaces/mybooking/baggage-request.interface';
import {
  FirstBaggage,
  FirstBaggageBoundMap,
  FirstBaggagePassengerApplyInfo,
} from '@common/interfaces/mybooking/mybooking.interface';
import { FirstBaggagePnr } from '@common/interfaces/mybooking/order-baggage.interface';
import { ServicingCommonStoreService } from '@common/services';
import { BaggageRequestService } from '@common/services/my-booking/baggage-request/baggage-request.service';
import { MyBookingServiceService } from '@common/services/my-booking/my-booking-service/my-booking-service.service';
import { MyBookingStoreService } from '@common/services/my-booking/my-booking-store.service';
import { MyBookingService } from '@common/services/my-booking/my-booking.service';
import { MasterJsonKeyPrefix } from '@conf/asw-master.config';
import { SupportModalBlockComponent } from '@lib/components/support-class';
import { DialogClickType, DialogType } from '@lib/interfaces';
import { StaticMsgPipe } from '@lib/pipes';
import { CommonLibService, DialogDisplayService, PageInitService } from '@lib/services';
import { of, Subscription } from 'rxjs';
import { Bound, GetOrderResponseData, GetOrderResponseDataOrderType, Traveler, Type1 } from 'src/sdk-servicing';

@Component({
  selector: 'asw-baggage-request',
  templateUrl: './baggage-request.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BaggageRequestComponent extends SupportModalBlockComponent {
  functionId = ServicingFunctionIdType.BOOKING_CONFIRMATION;

  pageId = ServicingPageIdType.MYBOOKING_SERVICE;

  subFunctionId = ServicingFunctionIdType.SERVICE_REQUEST;

  subPageId = ServicingPageIdType.BAGGAGE_REQUEST;

  pnr!: GetOrderResponseData;

  segmentInformationList: Type1[] = [];

  travelersByINF: Array<Traveler> = [];

  // 更新サービス情報
  updateServiceInformation: UpdateServiceInformation = {};

  bounds: Array<Bound> = [];

  screenInformationChangeFlag: boolean = false;

  subscription: Subscription = new Subscription();
  travelers: Array<Traveler> = [];

  flights: Array<Type1> = [];
  firstBaggage!: FirstBaggage;
  pageEndInitCalled: boolean = false;

  constructor(
    private _common: CommonLibService,
    private _changeDetector: ChangeDetectorRef,
    private _dialogService: DialogDisplayService,
    private _pageInitService: PageInitService,
    private _staticMsgPipe: StaticMsgPipe,
    private _myBookingService: MyBookingService,
    private _myBookingServiceService: MyBookingServiceService,
    private _myBookingStoreService: MyBookingStoreService,
    private _baggageRequestService: BaggageRequestService,
    private _servicingCommonStoreService: ServicingCommonStoreService
  ) {
    super(_common);
  }

  init() {
    // (1.1) 画面の初期化処理(G01-003)の初期化開始処理を呼び出す。
    this._pageInitService.startInit();
    this.subscription.add(
      this._myBookingServiceService.myBookingServiceMasterData$.subscribe((master) => {
        this._baggageRequestService.airportI18nAll = master.airportI18nAll ?? {};
      })
    );
    this.subscription.add(
      this._servicingCommonStoreService.getServicingCommonModel$().subscribe((servicingCommon) => {
        this.pnr = servicingCommon.getOrder ?? ({} as GetOrderResponseData);
        this.travelers = this.pnr?.travelers ?? [];
        this.bounds = this.pnr?.air?.bounds ?? [];
        this.firstBaggage = servicingCommon.firstBaggage ?? ({} as FirstBaggage);
        this.mappingInItBaggageRequest();
        this._changeDetector.detectChanges();
      })
    );
  }

  mappingInItBaggageRequest() {
    this.flights = this.bounds?.flatMap((bound: Bound) => bound.flights ?? ([] as Type1[])) ?? ([] as Type1[]);
    // (1.2) 画面情報に以下の内容を設定する。
    this.setInitialAswCommon();

    // (1.3) 画面にて変更した内容を保持するための「更新サービス情報」について以下の内容を設定し、保持する。
    // (1.3.1) 以下通りに「更新サービス情報」を作成する。
    this.updateServiceInformation = {
      boundsMap: new Map<string, SegmentMapData>(),
    };
    this.travelersByINF = filterTravelersByCondition(this.pnr, [
      {
        key: 'passengerTypeCode',
        value: PassengerType.INF,
        operator: notEqual,
      },
    ]);
    // (1.3.2) サービシング共通store.事前追加手荷物が存在する場合、サービシング共通store.事前追加手荷物.バウンドマップを更新サービス情報.バウンドマップに設定する。
    this.syncBaggagegStore();

    if (!this.pageEndInitCalled) {
      this._pageInitService.subDynamicInit(of({}));
      this._pageInitService.endInit(null);
      this.pageEndInitCalled = true;
    }
  }

  /** (1.2) 画面情報に以下の内容を設定する。*/
  setInitialAswCommon() {
    this._common.aswCommonStoreService.updateAswCommon({
      functionId: this.functionId,
      pageId: this.pageId,
      subFunctionId: this.subFunctionId,
      subPageId: this.subPageId,
      isEnabledLogin: false,
      isUpgrade: false,
    });
  }

  syncBaggagegStore() {
    for (let bounds of this.bounds ?? []) {
      for (let traveler of this.travelersByINF) {
        const travelerItem = (this.firstBaggage?.[bounds?.airBoundId ?? ''] as FirstBaggageBoundMap)?.[
          traveler?.id!
        ] as FirstBaggagePassengerApplyInfo;
        if (travelerItem) {
          this.addBaggageSelection(bounds?.airBoundId ?? '', traveler?.id ?? '', travelerItem);
        }
      }
    }
  }

  // (2) 閉じるボタン押下処理
  cancelEvent = () => {
    // (2.1) 更新サービス情報.バウンドマップ≠サービシング共通store.事前追加手荷物.バウンドマップの場合、パラメータとして"MSG0524"「選択した内容が無効になる旨」の確認ダイアログ(G02-005)を表示する。
    if (this.screenInformationChangeFlag) {
      this.openConfirmDialog();
    } else {
      // (2.2) 上記以外の場合、当モーダルを閉じる。
      this.close();
    }
  };

  openConfirmDialog(): void {
    this._dialogService
      .openDialog({ message: MasterJsonKeyPrefix.DYNAMIC + 'MSG0524' })
      .buttonClick$.subscribe((result) => {
        if (result.clickType === DialogClickType.CONFIRM) {
          this.close();
        }
      });
  }

  // [4] 出発地
  // [5] 到着地
  getAirportName(locationCode?: string, locationName?: string): string {
    let result = this._baggageRequestService.onHandleCommonNo1(locationCode);
    if (result) {
      return result;
    }
    return locationName ?? '';
  }

  isBaggageExist(airBoundId?: string, travelerId?: string): boolean {
    const firstBaggage = this.pnr?.services?.baggage?.firstBaggage as FirstBaggage;
    const segmentBaggageInformation = firstBaggage?.[airBoundId!];
    return isEmptyObject((segmentBaggageInformation as any)?.[travelerId!]) !== true;
  }

  getParamsPassengerName(traveler: Traveler) {
    return {
      '0': traveler?.names?.[0]?.title,
      '1': traveler?.names?.[0]?.firstName,
      '2': traveler?.names?.[0]?.lastName,
    };
  }

  // [7] 搭乗者名
  getPassengerName(traveler: Traveler) {
    return this._staticMsgPipe.transform('label.passenger.n1', this.getParamsPassengerName(traveler));
  }

  /**
   * [8] 同伴搭乗者姓名
   * 当該搭乗者情報.accompanyingTravelerId ≠ “”(空欄)(同伴搭乗者がいる)場合、以下内容を併せて表示する。
   * @param traveler
   * @returns
   */
  isAccompanyingTraveler(traveler?: Traveler): boolean {
    return Boolean(traveler?.accompanyingTravelerId);
  }

  /**
   * [8] 同伴搭乗者姓名
   * サービシング共通store.PNR情報取得API応答.data.travelersを繰り返し、当該搭乗者情報.accompanyingTravelerId = 当該travelers.idとなるtravelers.name[0]について、title + ” ”(半角スペース) + firstName + ” ”(半角スペース) + lastName
   * @param traveler
   * @returns
   */
  getAccompanyingPassenger(traveler?: Traveler): string {
    const children = this.travelers.find((travel) => travel?.id === traveler?.accompanyingTravelerId) ?? {};
    return isEmptyObject(children) !== true
      ? this._staticMsgPipe.transform('label.passenger.n1', this.getParamsPassengerName(children))
      : '';
  }

  // [9] 搭乗者種別
  getPassengerLabel(code?: string): string {
    return code ? this._staticMsgPipe.transform(getPassengerLabel(code)) : '';
  }

  getAccompanyingPassengerLabel(traveler?: Traveler): string {
    const children = this.travelers.find((travel) => travel?.id === traveler?.accompanyingTravelerId) ?? {};
    return children?.passengerTypeCode
      ? this._staticMsgPipe.transform(getPassengerLabel(children?.passengerTypeCode ?? ''))
      : '';
  }

  // (3) 申込ボタン押下処理
  applyEvent = async () => {
    // (3.1) GDS PNRかつ、事前追加手荷物を選択した搭乗者にリレートしている、かつセグメントにリレートしていないEMD情報が存在するか判定する。
    const isStopProcess = await this.GDSRelatedToThePassenger();
    if (isStopProcess) return;
    // (3.2) 以下の内容をサービシング共通store.事前追加手荷物に設定する
    const firstBaggage: FirstBaggage = this.convertFromInternalToStore();
    // (3.3) 以下の内容をサービシング共通storeで保持する。
    this._servicingCommonStoreService.updateServicingCommon({
      // (3.2) 以下の内容をサービシング共通store.事前追加手荷物に設定する
      firstBaggage: firstBaggage,
      // (3.3) 以下の内容をサービシング共通storeで保持する。
      initDisplayService: 'firstBaggage',
      isFirstDisplay: false,
    });
    // (3.4) 当モーダルを閉じ、予約詳細(S01-P030)の[初期表示処理]を呼び出す。
    this._myBookingService.runOnInit();
    this.close();
  };

  GDSRelatedToThePassenger = async (): Promise<boolean> => {
    for (let bound of this.bounds) {
      const boundId = String(bound.airBoundId);
      const segmentMap = this.updateServiceInformation?.boundsMap?.get(boundId);

      if (!segmentMap) continue;
      // (3.1.1) 更新サービス情報.バウンドマップに当該バウント情報.idをキーとするオブジェクトが存在しない場合、次のバウンド情報の繰り返しを行う。
      // (3.1.2) 当該バウンド情報.idをキーとする更新サービス情報.バウンドマップを対象バウンドマップとして保持し、ここから処理を行う。
      for (let traveler of this.travelers) {
        const travelerId = String(traveler?.id);
        // (3.1.3) サービシング共通store.PNR情報取得API応答.data.serviceSummary.emds.<当該搭乗者情報.id>が存在しない場合、次の搭乗者情報の繰り返しを行う。
        const passengerRelatedInformation = (this.pnr?.serviceSummary?.emds as any)?.[travelerId];

        if (!passengerRelatedInformation) continue;

        if (segmentMap?.passengerApplicationStatusMap?.has(travelerId) !== true) continue;

        // (3.1.6) 当該搭乗者情報.idをキーとする対象バウンドマップ.搭乗者別申込状況マップが存在する場合、以下処理を行う。
        if (
          this.pnr?.orderType?.channelType === GetOrderResponseDataOrderType.ChannelTypeEnum.Gds &&
          passengerRelatedInformation?.firstBaggage?.hasEmd &&
          passengerRelatedInformation?.firstBaggage?.hasSegmentAssociatedEmd !== true
        ) {
          // (3.1.7) 以下条件をすべて満たす場合、パラメータとして"MSG0563"「過去に発行したEMDは後日払戻される旨」の確認ダイアログ(G02-005)を表示する。
          const clickType = await this._myBookingServiceService.openDialogPromise('MSG0563');
          // (3.1.8) 確認ダイアログより閉じるボタン押下が返却された場合、処理を終了する。
          if (clickType === DialogClickType.CLOSE) {
            return true;
          }
          // (3.1.9) 確認ダイアログより確定ボタン押下が返却された場合、以降の処理を続行する。
          else if (clickType === DialogClickType.CONFIRM) return false;
        }
      }
    }
    return false;
  };

  convertFromInternalToStore(): FirstBaggage {
    let firstBaggage: FirstBaggage = {};
    let totalAmount = 0;
    for (const [airBoundId, airBoundfo] of this.updateServiceInformation?.boundsMap! ?? []) {
      for (const [travelerId, travelerInfo] of airBoundfo?.passengerApplicationStatusMap ?? []) {
        const boundMap = firstBaggage?.[airBoundId] as FirstBaggageBoundMap;
        const travelerMap = boundMap?.[travelerId] as FirstBaggagePassengerApplyInfo;
        totalAmount += travelerInfo?.paidServiceAmount ?? 0;
        firstBaggage = {
          ...firstBaggage,
          [airBoundId]: {
            ...boundMap,
            segmentId: airBoundfo.segmentId,
            [travelerId]: {
              ...travelerMap,
              paidServiceAmount: travelerInfo?.paidServiceAmount ?? 0,
              updateCategory: travelerInfo?.updateCategory,
            },
          },
        };
      }
    }
    firstBaggage.isApplicationProcessed = true;
    firstBaggage.totalAmount = totalAmount;
    return firstBaggage;
  }

  // (4) 手荷物選択処理
  baggageSelectionValueChangeProcessing(changeBaggageData: ChangeBaggageData): void {
    // (4.1) 画面.手荷物選択をチェックONの場合、更新サービス情報を以下の通り更新する。
    if (changeBaggageData.isCheck) {
      const passengerData: PassengerApplicationStatusMap = {
        paidServiceAmount: changeBaggageData.paidServiceAmount,
        updateCategory: 'request',
      };
      this.addBaggageSelection(changeBaggageData.airBoundId, changeBaggageData.travelerId, passengerData);
    }
    // (4.2) 画面.手荷物選択をチェックOFFの場合、更新サービス情報を以下の通り更新する。
    else {
      this.removeBaggageSelection(changeBaggageData.airBoundId, changeBaggageData.travelerId);
    }
    // (4.3) 申込ボタンの状態を以下の通りに切り替える。
    this.screenInformationChangeFlag = this.isApplyActive();
  }

  addBaggageSelection(airBoundId: string, travelerId: string, passengerData: PassengerApplicationStatusMap) {
    if (this.updateServiceInformation?.boundsMap?.has(airBoundId)) {
      this.updateServiceInformation?.boundsMap
        ?.get(airBoundId)
        ?.passengerApplicationStatusMap?.set(travelerId, passengerData);
    } else {
      const updateBound = this.bounds?.find((item) => item.airBoundId === airBoundId);
      this.updateServiceInformation?.boundsMap?.set(airBoundId, {
        segmentId: updateBound?.flights?.[0]?.id!,
        passengerApplicationStatusMap: new Map<string, PassengerApplicationStatusMap>(),
      });
      this.updateServiceInformation?.boundsMap
        ?.get(airBoundId)
        ?.passengerApplicationStatusMap?.set(travelerId, passengerData);
    }
  }

  removeBaggageSelection(airBoundId: string, travelerId: string) {
    let boundsMap = this.updateServiceInformation?.boundsMap;
    if (boundsMap?.has(airBoundId)) {
      boundsMap?.get(airBoundId)!.passengerApplicationStatusMap.delete(travelerId);
      if (boundsMap?.get(airBoundId)!.passengerApplicationStatusMap.size === 0) {
        boundsMap?.delete(airBoundId);
      }
    }
    this.updateServiceInformation = {
      boundsMap: boundsMap,
    };
  }

  // [15] 申込ボタン
  isApplyActive(): boolean {
    const bounds = this.pnr?.air?.bounds! ?? [];
    return bounds.some((bound) => {
      return this.travelersByINF.some((traveler) => {
        return this.isTravelerStatusDifferent(bound, traveler);
      });
    });
  }

  private isTravelerStatusDifferent(bound: Bound, traveler: Traveler): boolean {
    const travelerStoreItem = (this.firstBaggage?.[bound?.airBoundId!] as FirstBaggageBoundMap)?.[
      traveler?.id!
    ] as FirstBaggagePassengerApplyInfo;
    const travelerUpdateItem = this.updateServiceInformation?.boundsMap
      ?.get(bound?.airBoundId ?? '')
      ?.passengerApplicationStatusMap?.get(traveler?.id ?? '');
    return (
      JSON.stringify(travelerStoreItem ?? {}, Object.keys(travelerStoreItem ?? {}).sort()) !==
      JSON.stringify(travelerUpdateItem ?? {}, Object.keys(travelerUpdateItem ?? {}).sort())
    );
  }
  reload() {}

  destroy() {
    this._servicingCommonStoreService.updateServicingCommon({
      previousPageIdentifier: PREVIOUS_PAGE_IDENTIFIER.BAGGAGE_REQUEST,
    });
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}
